// A copy tree whose text always comes from the translator currently bound, read at the moment it is used.
//
// CRM's copy is authored as factories over a translator (`createCrmCopy(t)`) and read through module-level
// handles such as `const COPY = CRM_COPY.dealForm`. A live tree keeps those handles cheap and stable while the
// text itself is resolved per read from the copy built for the active locale:
//   - a group (`CRM_COPY.dealForm`) is a stable handle and never reads text;
//   - a formatter (`COPY.counts.tasks`) is a stable function that formats with the bound copy when called;
//   - text (`COPY.titles.create`) and lists are read from the bound copy, and reading them before a translator
//     is bound throws, so copy captured at import time is a test failure rather than a frozen locale.

type Translator<T> = (key: string, options?: { values?: Record<string, string | number | Date | null | undefined> }) => T;

export interface LiveCopy<Copy extends object> {
  /** The live tree: stable handles over whatever copy is bound when it is read. */
  readonly copy: Copy;
  /** Binds the copy built from this translator. Rebinding the same translator keeps the built copy. */
  bind(translate: Translator<string>): Copy;
  /** Clears the binding (tests). */
  unbind(): void;
  /** Whether a translator is bound. */
  isBound(): boolean;
}

const isGroup = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function createLiveCopy<Copy extends object>(
  build: (translate: Translator<string>) => Copy,
  name = 'copy'
): LiveCopy<Copy> {
  // The tree's shape (groups, formatters, text) does not depend on the locale, so it is read once from a
  // translator that returns the key.
  const shape = build(key => key);
  let boundTranslate: Translator<string> | null = null;
  let bound: Copy | null = null;

  const read = (path: readonly string[]): unknown => {
    if (!bound) {
      throw new Error(`${name}.${path.join('.')} was read before a translator was bound`);
    }
    let value: unknown = bound;
    for (const segment of path) value = (value as Record<string, unknown>)[segment];
    return value;
  };

  const handles = new Map<string, unknown>();
  const handle = (path: readonly string[], shapeValue: unknown): unknown => {
    const id = path.join('.');
    const existing = handles.get(id);
    if (existing) return existing;
    let created: unknown;
    if (isGroup(shapeValue)) created = group(path, shapeValue);
    else if (typeof shapeValue === 'function') {
      created = (...args: unknown[]) => (read(path) as (...input: unknown[]) => unknown)(...args);
    }
    handles.set(id, created);
    return created;
  };

  function group(path: readonly string[], shapeGroup: Record<string, unknown>): object {
    return new Proxy(
      {},
      {
        get(_target, property) {
          if (typeof property !== 'string' || !Object.hasOwn(shapeGroup, property)) return undefined;
          const shapeValue = shapeGroup[property];
          const next = [...path, property];
          return isGroup(shapeValue) || typeof shapeValue === 'function'
            ? handle(next, shapeValue)
            : read(next);
        },
        has: (_target, property) => typeof property === 'string' && Object.hasOwn(shapeGroup, property),
        ownKeys: () => Object.keys(shapeGroup),
        getOwnPropertyDescriptor: (_target, property) =>
          typeof property === 'string' && Object.hasOwn(shapeGroup, property)
            ? { configurable: true, enumerable: true, writable: false, value: undefined }
            : undefined,
        set: () => false,
        defineProperty: () => false,
        deleteProperty: () => false,
      }
    );
  }

  return {
    copy: group([], shape as Record<string, unknown>) as Copy,
    bind(translate) {
      if (translate !== boundTranslate || !bound) {
        bound = build(translate);
        boundTranslate = translate;
      }
      return bound;
    },
    unbind() {
      bound = null;
      boundTranslate = null;
    },
    isBound: () => bound !== null,
  };
}
